import { inject, Injectable } from '@angular/core';
import { interval } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { EnvironmentService } from '../environment.service';
import { HostApplicationService } from '../host-application.service';
import { HostAppContext } from '../host-application.interfaces';

@Injectable({
  providedIn: 'root'
})
export class GainsightAnalyticsService {
  #hostApplicationService = inject(HostApplicationService);
  #environmentService = inject(EnvironmentService);

  /** Resolves once the SDK load + identify attempt has completed (or was skipped). */
  #ready?: Promise<void>;

  public init(): Promise<void> {
    this.#ready ??= this.#doInit();
    return this.#ready;
  }

  /**
   * Resolves once {@link init} has finished (script loaded and `identify`
   * attempted). Use this before firing early events (e.g. on app open) so they
   * are not queued ahead of `identify` and dropped by Gainsight PX.
   */
  public whenReady(): Promise<void> {
    return this.#ready ?? Promise.resolve();
  }

  async #doInit(): Promise<void> {
    try {
      if (this.#environmentService.gainsightPxKey) {
        await this.#setScript();

        if (typeof aptrinsic !== 'undefined') {
          const context = await this.#hostApplicationService.getCurrentContext();
          if (context) {
            await this.#configure(context);
          } else {
            console.log("Gainsight configuration skipped - no context available");
          }
        }
      } else {
        console.warn('No Gainsight tag key');
      }
    } catch (e) {
      console.error(e);
    }
  }

  async #setScript() {
    const sdkName = 'aptrinsic';
    // @ts-ignore
    window[sdkName] = window[sdkName] || function (...args: any[]) {
      // @ts-ignore
      // Push args as a single queue entry (matches Gainsight's official
      // `.push(arguments)`); spreading corrupts calls queued before SDK load.
      (window[sdkName].q = window[sdkName].q || []).push(args);
    };
    // @ts-ignore
    window[sdkName].p = this.#environmentService.gainsightPxKey;
    // @ts-ignore
    // Must stay false: the embedded PX instance reports events directly. Enabling
    // iframe mode makes PX defer to the host page's PX instance, so Aura's events
    // are swallowed by the host (e.g. VisionPro) instead of its own product.
    window[sdkName].c = { iframeModeEnabled: false };

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://web-sdk-eu.aptrinsic.com/api/aptrinsic.js?a=' + this.#environmentService.gainsightPxKey;
    document.head.appendChild(script);

    await new Promise<void>((resolve, reject) => {
      script.onload = () => {
        console.log('Gainsight SDK loading...');
        if (aptrinsic.init) {
          console.log('Gainsight SDK initialized: ', aptrinsic.init);

          resolve();
          return;
        }

        // Fallback in case script load was delayed
        const sub = interval(250).pipe(take(5), finalize(() => {
          if (!aptrinsic.init) {
            reject(new Error("Gainsight failed to initialize after 5 retries."));
          }
        })).subscribe(() => {
          console.log('Gainsight SDK init delayed retry: ', aptrinsic.init);
          if (aptrinsic.init) {
            sub.unsubscribe();
            resolve();
          }
        });
      };
      script.onerror = () => {
        // The script may have been blocked by an ad blocker
        reject(new Error("Gainsight failed to load the script. Please disable ad blockers or add site to the excluded list."));
      };
    });
  }

  async #configure(context: HostAppContext): Promise<void> {
    console.debug('Configuring Gainsight...');

    const base = context['embedded.aura.unityaiot.com'];
    const account = this.resolveAccountDetails(context);

    aptrinsic('identify',
      {
        id: base?.user_id,
        email: base?.user_email,
        firstName: base?.user_first_name,
        lastName: base?.user_last_name,
      },
      {
        id: account.id,
        name: account.name,
      }
    );
    console.debug("Gainsight is initialized: ", aptrinsic.init);
  }

  /**
   * Resolves the account (organisation) identity used for Gainsight `identify`.
   * The base implementation reads the FC and On-Road platform sections; subclasses
   * may override to support additional platforms.
   */
  protected resolveAccountDetails(context: HostAppContext): { id: string; name: string } {
    let id = "N/A";
    let name = "N/A";
    if (Object.hasOwn(context, 'fc.fleetcomplete.com')) {
      id = context['fc.fleetcomplete.com']?.org_id || 'N/A';
      name = context['fc.fleetcomplete.com']?.org_name || 'N/A';
    }
    else if (Object.hasOwn(context, 'on_road.unityaiot.com')) {
      id = context['on_road.unityaiot.com']?.org_id || 'N/A';
      name = context['on_road.unityaiot.com']?.org_name || 'N/A';
    }
    return { id, name };
  }

  public trackEvent(eventName: string, properties: Record<string, string | number | boolean> = {}): void {
    try {
      if (typeof aptrinsic !== 'undefined') {
        aptrinsic('track', eventName, properties);
      }
    } catch (e) {
      console.error('Gainsight trackEvent error:', e);
    }
  }
}
